/**
 * 会员菜品管理器
 * 统一管理会员菜品相关功能，包括会员菜品显示、选择、限制和消费门槛验证等
 * 采用单例模式确保全局唯一实例
 */
import ChannelManager, { ChannelType } from '@/utils/channel-manager'
import LocalStorageManager from '@/utils/local-storage-manager'
import { getUserMemberInfo, getPointsRule } from '@/views/points/api'
import MenuManager from '@/utils/menu-manager'
import RestaurantInfoManager from '@/views/restaurant-information/utils/restaurant-info-manager'
import { message, Modal } from 'ant-design-vue'
import CategoryType from '@/utils/menu-manager/models/enum/CategoryType'
import OrderedItemType from '@/utils/ordered-item-manager/models/OrderedItemType'
import UserMenu from '@/utils/menu-manager/models/user-menu/UserMenu'
import OrderMenu from '@/utils/menu-manager/models/menu'
import { Item } from '@/utils/menu-manager/models/item'
import {
  MEMBER_ITEM_CATEGORY_NAME,
  MemberItemLocaleKeys
} from './models/constants'
import { checkMemberFreeItemsUsed } from './api'
import RouterUtil from '@/utils/helper/RouterUtil'

// 导入OrderMenuCategory类型
type OrderMenuCategory = {
  items: Item[]
  categoryId: string
  name: string
  translations: Record<string, string>
}

/**
 * 会员菜品数据接口
 */
export interface MemberItemData {
  userLevel: number
  threshold: number
  memberItems: Item[]
  isMemberItemsLoaded: boolean
  lastUpdated?: number // 新增缓存时间戳字段
}

/**
 * 会员菜品管理器配置接口
 */
export interface MemberItemManagerConfig {
  // 缓存过期时间（毫秒）
  cacheExpirationMs?: number
  // 错误重试次数
  maxRetryCount?: number
  // 重试延迟（毫秒）
  retryDelayMs?: number
}

/**
 * 错误类型枚举
 */
export enum MemberItemErrorType {
  NETWORK = 'NETWORK',
  API = 'API',
  VALIDATION = 'VALIDATION',
  UNKNOWN = 'UNKNOWN'
}

/**
 * 会员菜品操作结果接口
 */
export interface MemberItemResult<T> {
  success: boolean
  data?: T
  error?: {
    type: MemberItemErrorType
    message: string
    originalError?: any
  }
}

/**
 * 检查门槛结果接口
 */
export interface ThresholdCheckResult {
  passed: boolean
  threshold: number
  diff: number
}

/**
 * 状态变更事件类型
 */
export enum MemberItemEventType {
  CART_CHANGED = 'CART_CHANGED',
  ORDER_CHANGED = 'ORDER_CHANGED',
  USED_STATUS_CHANGED = 'USED_STATUS_CHANGED',
  USER_LEVEL_CHANGED = 'USER_LEVEL_CHANGED'
}

/**
 * 状态变更事件回调函数类型
 */
export type MemberItemEventCallback = (state: any) => void

/**
 * 会员菜品管理器类
 * 负责会员菜品的显示、选择和消费门槛验证等功能
 */
export default class MemberItemManager {
  // 单例实例
  private static _instance: MemberItemManager

  // 会员菜品数据缓存（shopId -> 会员菜品数据）
  private _memberItemDataMap: Map<string, MemberItemData> = new Map()

  // 会员菜品状态追踪
  private _memberItemState = {
    inCart: false,
    inUnpaidOrder: false,
    countUnpaidOrder: 0,
    usedToday: false
  }

  // 会员等级
  private _userLevel = 0
  private _maxLevel = 0

  // 初始化状态标志
  private _initialized = false

  // 配置参数
  private _config: MemberItemManagerConfig = {
    cacheExpirationMs: 30 * 60 * 1000, // 默认30分钟过期
    maxRetryCount: 2,
    retryDelayMs: 1000
  }

  // 事件监听器
  private _eventListeners: Map<
    MemberItemEventType,
    Set<MemberItemEventCallback>
  > = new Map()

  /**
   * 获取单例实例
   * @param config 可选配置参数
   * @returns MemberItemManager实例
   */
  public static instance(config?: MemberItemManagerConfig): MemberItemManager {
    if (!this._instance) {
      this._instance = new MemberItemManager()
    }

    // 如果提供了配置参数，更新现有实例的配置
    if (config) {
      this._instance.updateConfig(config)
    }

    return this._instance
  }

  /**
   * 私有构造函数，防止外部直接创建实例
   */
  private constructor() {
    this._memberItemDataMap = new Map<string, MemberItemData>()
  }

  /**
   * 更新配置参数
   * @param config 新的配置参数
   */
  public updateConfig(config: Partial<MemberItemManagerConfig>): void {
    this._config = {
      ...this._config,
      ...config
    }
  }

  /**
   * 初始化状态
   * 异步初始化会员等级和当天使用状态
   */
  public async initStatus(): Promise<MemberItemResult<void>> {
    try {
      this._memberItemState = {
        inCart: false,
        inUnpaidOrder: this._memberItemState.inUnpaidOrder,
        countUnpaidOrder: this._memberItemState.countUnpaidOrder,
        usedToday: this._memberItemState.usedToday
      }
      this._userLevel = 0

      // 并行获取会员等级和使用状态
      await Promise.all([
        this.getUserLevel().catch((error) => {
          console.error('初始化会员等级失败', error)
          return 0
        }),
        this.getMaxLevel().catch((error) => {
          console.error('初始化最大会员等级失败', error)
          return 0
        }),
        this.hasUsedMemberItemToday().catch((error) => {
          console.error('初始化使用状态失败', error)
          return false
        })
      ])

      this._initialized = true

      return { success: true }
    } catch (error) {
      console.error('初始化状态失败', error)
      return {
        success: false,
        error: {
          type: MemberItemErrorType.UNKNOWN,
          message: '初始化会员菜品状态失败',
          originalError: error
        }
      }
    }
  }

  /**
   * 获取最大会员等级
   * @returns 最大会员等级
   */
  public async getMaxLevel(): Promise<number> {
    if (this._maxLevel > 0) return this._maxLevel

    const { data } = await getPointsRule()
    this._maxLevel = data.reduce((max, rule) => Math.max(max, rule.level), 0)
    return this._maxLevel
  }

  // 获取属性值
  public get userLevel(): number {
    return this._userLevel
  }

  /**
   * 确保初始化完成
   * @returns Promise that resolves when initialization is complete
   */
  private async ensureInitialized(): Promise<void> {
    if (!this._initialized) {
      const result = await this.initStatus()
      if (!result.success) {
        throw new Error(result.error?.message || '初始化失败')
      }
    }
  }

  /**
   * 检查当前环境是否支持查看会员菜品
   * 条件：用户已登录 + Circle Eats渠道 + 会员等级大于0
   * @returns 是否可以查看会员菜品
   */
  public canViewMemberItems(): boolean {
    // 检查当前渠道是否为Circle Eats
    const isCircleEatsChannel =
      ChannelManager.instance().getChannel() === ChannelType.CIRCLE_EATS

    // 获取登录状态
    const isLoggedIn = new LocalStorageManager().userId.length > 0

    // 只有当用户已登录且当前渠道是Circle Eats且会员等级大于0时，才能查看会员菜品
    return isLoggedIn && isCircleEatsChannel && this._userLevel > 0
  }

  /**
   * 检查当前是否可以添加会员菜品
   * 条件：未完成订单中不存在会员菜品 + 当天未使用过会员菜品 + 购物车中不存在会员菜品
   * @returns {boolean} 是否可以添加会员菜品
   */
  public async canAddMemberItem(): Promise<boolean> {
    await this.hasUsedMemberItemToday()
    return (
      !this._memberItemState.inUnpaidOrder &&
      !this._memberItemState.usedToday &&
      !this._memberItemState.inCart
    )
  }

  /**
   * 检查是否可以替换会员菜品
   * 条件：未完成订单中不存在会员菜品 + 当天未使用过会员菜品
   * @param item 会员菜品
   * @returns 是否可以替换会员菜品
   */
  public async canReplaceMemberItem(): Promise<boolean> {
    await this.hasUsedMemberItemToday()
    return (
      !this._memberItemState.inUnpaidOrder && !this._memberItemState.usedToday
    )
  }

  /**
   * 检查是否可以支付
   * @param items 订单商品列表
   * @param t 翻译函数
   * @returns 是否可以支付
   */
  public canPay(items: OrderedItemType[], t: (key: string) => string): boolean {
    if (!items?.length) return false
    // 如果存在会员菜品去支付，则必须先登录 且 今天的会员菜品没有使用过 且 菜品等级小于等于当前会员等级
    // 否则直接返回true
    let result = true

    const isLoggedIn = new LocalStorageManager().userId.length > 0

    if (this.hasMemberItemInItems(items) && !isLoggedIn) {
      Modal.error({
        title: t(MemberItemLocaleKeys.TIP),
        content: t(MemberItemLocaleKeys.PLEASE_LOGIN_TO_USE_MEMBER_ITEM),
        onOk: () => {
          RouterUtil.instance().push({ name: 'login' })
        }
      })
      result = false
      return result
    }

    return true

    // if (!memberItem) return true

    // if (this._memberItemState.usedToday) {
    //   message.info(t(MemberItemLocaleKeys.MEMBER_ITEM_USED_TODAY))
    //   result = false
    // }

    // const lv = Math.max(
    //   Number(memberItem.categoryId),
    //   Number(memberItem?.originalCategoryId || 0)
    // )

    // if (lv > this._userLevel) {
    //   message.info(t(MemberItemLocaleKeys.LEVEL_NOT_ENOUGH))
    //   result = false
    // }

    // return result
  }

  /**
   * 获取会员等级
   * @returns 会员等级，未登录或获取失败时返回0
   */
  public async getUserLevel(): Promise<number> {
    const localStorageManager = new LocalStorageManager()
    const isLoggedIn = localStorageManager.userId.length > 0
    if (!isLoggedIn) {
      this._userLevel = 0
      return this._userLevel
    }

    let retryCount = 0
    let lastError: any

    while (retryCount <= this._config.maxRetryCount!) {
      try {
        const shopId = RestaurantInfoManager.instance().shopId
        // 如果没有有效的shopId，返回0
        if (!shopId) {
          console.warn('无法获取用户会员等级：缺少shopId')
          this._userLevel = 0
          return this._userLevel
        }
        const { data: userMemberInfo } = await getUserMemberInfo(shopId)
        const newLevel = userMemberInfo?.level || 0

        // 如果等级发生变化，触发事件
        if (this._userLevel !== newLevel) {
          const oldLevel = this._userLevel
          this._userLevel = newLevel
          this.emitEvent(MemberItemEventType.USER_LEVEL_CHANGED, {
            oldLevel,
            newLevel
          })
        } else {
          this._userLevel = newLevel
        }

        return this._userLevel
      } catch (error) {
        lastError = error
        console.error(
          `获取会员信息失败 (尝试 ${retryCount + 1}/${
            this._config.maxRetryCount! + 1
          })`,
          error
        )

        if (retryCount < this._config.maxRetryCount!) {
          // 等待一段时间后重试
          await new Promise((resolve) =>
            setTimeout(resolve, this._config.retryDelayMs)
          )
          retryCount++
        } else {
          break
        }
      } finally {
        this.getMemberItemThreshold(
          RestaurantInfoManager.instance().shopId,
          this._userLevel
        )
      }
    }

    console.error('获取会员信息失败，已达到最大重试次数', lastError)
    return this._userLevel
  }

  /**
   * 获取会员菜品消费门槛
   * @param shopId 店铺ID
   * @param userLevel 会员等级
   * @returns 消费门槛金额（单位：分）
   */
  public async getMemberItemThreshold(
    shopId: string,
    userLevel: number
  ): Promise<number> {
    // 默认门槛0元不限制
    let threshold = 0

    try {
      // 检查缓存中是否已有数据且未过期
      const cachedData = this._memberItemDataMap.get(shopId)
      const now = Date.now()

      if (
        cachedData &&
        cachedData.threshold > 0 &&
        cachedData.lastUpdated &&
        now - cachedData.lastUpdated < this._config.cacheExpirationMs!
      ) {
        return cachedData.threshold
      }

      if (!shopId) {
        shopId = RestaurantInfoManager.instance().shopId
      }

      if (shopId) {
        const userMenu = await MenuManager.instance().fetchUserMenu(shopId)
        if (userMenu?.levelThresholdList) {
          const levelThresholdList = userMenu.levelThresholdList
          if (userLevel > 0 && userLevel <= levelThresholdList.length) {
            threshold =
              levelThresholdList[userLevel - 1]?.thresholdAmount || threshold
          }
        }

        // 更新缓存，添加时间戳
        const existingData = this._memberItemDataMap.get(shopId) || {
          userLevel,
          threshold: 0,
          memberItems: [],
          isMemberItemsLoaded: false
        }

        this._memberItemDataMap.set(shopId, {
          ...existingData,
          threshold,
          lastUpdated: now
        })
      }
    } catch (error) {
      console.error('获取会员消费门槛失败', error)
    }

    return threshold
  }

  /**
   * 检查购物车是否满足会员菜品消费门槛
   * @param items 订单商品列表
   * @param shopId 店铺ID
   * @returns 验证结果 {passed: boolean, threshold: number}
   */
  public async checkMemberItemThreshold(
    items: OrderedItemType[],
    shopId?: string
  ): Promise<ThresholdCheckResult> {
    try {
      // 确保初始化完成
      await this.ensureInitialized()

      // 检查购物车中是否有会员菜品
      if (!this._memberItemState.inCart) {
        return { passed: true, threshold: 0, diff: 0 }
      }

      // 会员等级为0不能选择会员菜品
      if (this._userLevel === 0) {
        return { passed: false, threshold: 0, diff: 0 }
      }

      // 获取消费门槛
      const finalShopId =
        shopId || RestaurantInfoManager.instance().shopId || ''
      const threshold = await this.getMemberItemThreshold(
        finalShopId,
        this._userLevel
      )

      // 计算购物车总金额（不包括会员菜品）
      const cartTotal =
        this.calculateNonMemberItemsTotal(items) +
        this._memberItemState.countUnpaidOrder

      // 检查是否满足门槛
      return {
        passed: cartTotal >= threshold,
        threshold,
        diff: cartTotal - threshold
      }
    } catch (error) {
      console.error('检查会员菜品门槛失败', error)
      return { passed: false, threshold: 0, diff: 0 }
    }
  }

  /**
   * 计算非会员菜品的总金额
   * @param items 订单商品列表
   * @returns 总金额（单位：分）
   */
  private calculateNonMemberItemsTotal(items: OrderedItemType[]): number {
    return items
      .filter((item) => !this.isMemberCategoryId(item.categoryId))
      .reduce((sum, item) => sum + item.price * item.quantity, 0)
  }

  /**
   * 检查当天是否已使用过会员菜品
   * @returns 是否已使用过会员菜品
   */
  public async hasUsedMemberItemToday(): Promise<boolean> {
    const isLoggedIn = new LocalStorageManager().userId.length > 0
    if (!isLoggedIn) return false
    try {
      let retryCount = 0
      while (retryCount <= this._config.maxRetryCount!) {
        try {
          // 调用API检查
          const { data } = await checkMemberFreeItemsUsed()
          const newUsedStatus = Boolean(data)

          // 如果状态发生变化，触发事件
          if (this._memberItemState.usedToday !== newUsedStatus) {
            const oldStatus = this._memberItemState.usedToday
            this._memberItemState.usedToday = newUsedStatus
            this.emitEvent(MemberItemEventType.USED_STATUS_CHANGED, {
              oldStatus,
              newStatus: newUsedStatus
            })
          } else {
            // 缓存结果
            this._memberItemState.usedToday = newUsedStatus
          }

          return this._memberItemState.usedToday
        } catch (error) {
          console.error(
            `检查会员菜品使用情况失败 (尝试 ${retryCount + 1}/${
              this._config.maxRetryCount! + 1
            })`,
            error
          )

          if (retryCount < this._config.maxRetryCount!) {
            // 等待一段时间后重试
            await new Promise((resolve) =>
              setTimeout(resolve, this._config.retryDelayMs)
            )
            retryCount++
          } else {
            throw error
          }
        }
      }

      // 达到最大重试次数仍失败
      return false
    } catch (error) {
      console.error('检查会员菜品使用情况失败，已达到最大重试次数', error)
      return false
    }
  }

  /**
   * 标记会员菜品已存在于未支付订单中
   * @param added 是否已添加
   */
  public setMemberItemInUnpaidOrder(added: boolean): void {
    const oldStatus = this._memberItemState.inUnpaidOrder
    this._memberItemState.inUnpaidOrder = added

    if (oldStatus !== added) {
      this.emitEvent(MemberItemEventType.ORDER_CHANGED, {
        oldStatus,
        newStatus: added
      })
    }
  }

  /**
   * 检查当前是否已添加会员菜品到未支付订单中
   * @returns 是否已添加会员菜品
   */
  public hasMemberItemInUnpaidOrder(): boolean {
    return this._memberItemState.inUnpaidOrder
  }

  /**
   * 标记购物车中是否存在会员菜品
   * @param added 是否存在
   */
  public setMemberItemInCart(added: boolean): void {
    const oldStatus = this._memberItemState.inCart
    this._memberItemState.inCart = added

    if (oldStatus !== added) {
      this.emitEvent(MemberItemEventType.CART_CHANGED, {
        oldStatus,
        newStatus: added
      })
    }
  }

  /**
   * 获取购物车中是否存在会员菜品
   * @returns 是否存在
   */
  public hasMemberItemInCart(): boolean {
    return this._memberItemState.inCart
  }

  /**
   * 检查订单项列表中是否包含会员菜品
   * @param items 订单项列表
   * @returns 是否包含会员菜品
   */
  public checkMemberItemInOrderItems(items: OrderedItemType[]): boolean {
    // 否则检查订单项
    const hasMemberItem = this.hasMemberItemInItems(items)
    this.setMemberItemInUnpaidOrder(hasMemberItem)

    // 计算未支付订单总价
    const totalPrice = items
      .filter((item) => !item.orderedItemVoid)
      .reduce((sum, item) => sum + item.price * item.quantity, 0)
    if (totalPrice > 0) {
      this._memberItemState.countUnpaidOrder = totalPrice
    }
    return this._memberItemState.inUnpaidOrder
  }

  /**
   * 检查购物车中是否存在会员菜品
   * @param items 订单商品列表
   * @returns 是否存在会员菜品
   */
  public checkMemberItemInCart(items: OrderedItemType[]): boolean {
    const hasMemberItem = this.hasMemberItemInItems(items)
    this.setMemberItemInCart(hasMemberItem)
    return hasMemberItem
  }

  /**
   * 检查商品列表中是否存在会员菜品
   * @param items 订单商品列表
   * @returns 是否有会员菜品
   */
  public hasMemberItemInItems(items: OrderedItemType[]): boolean {
    return items.some((item) => {
      // console.log('退菜', item.orderedItemVoid)
      // 2 - 是赠送的退菜  不是2 - 正常退菜
      return (
        this.isMemberCategoryId(item.categoryId) &&
        !(item.orderedItemVoid && item.orderedItemVoid?.voidReasonId !== '2')
      )
    })
  }

  /**
   * 检查分类ID是否是会员菜品分类
   * @param categoryId 分类ID
   * @returns 是否是会员分类
   */
  public isMemberCategoryId(categoryId: string): boolean {
    return (
      categoryId === CategoryType.MemberItem ||
      (categoryId.length === 1 &&
        !isNaN(Number(categoryId)) &&
        Number(categoryId) > 0 &&
        Number(categoryId) <= this._maxLevel)
    )
  }

  /**
   * 获取菜品categoryId中最大的会员分类id且不超过当前会员等级
   * @param categoryIds 菜品分类ID列表
   * @returns 最大的会员菜品分类id
   */
  public getMaxCategoryId(categoryIds: string[]): string {
    const memberCategoryIds = categoryIds.filter((id) =>
      this.isMemberCategoryId(id)
    )
    // 必须包含会员分类id
    if (memberCategoryIds.length === 0) return ''

    // 获取最大的会员等级 不能超过当前会员等级
    const maxLevel = Math.max(...memberCategoryIds.map(Number))
    if (maxLevel > this._userLevel) return this._userLevel.toString()

    return maxLevel.toString()
  }

  /**
   * 创建会员菜品分类
   * 根据会员等级和渠道筛选会员菜品，设置价格为0，并构建会员菜品分类
   * @param userMenu 用户菜单信息
   * @param orderMenu 订单菜单
   * @returns 是否成功添加会员菜品分类
   */
  public async createMemberItemsCategory(
    userMenu: UserMenu,
    orderMenu: OrderMenu
  ): Promise<boolean> {
    try {
      // 确保初始化完成
      await this.ensureInitialized()

      // 检查是否可以查看会员菜品
      if (!this.canViewMemberItems()) return false
      if (this._userLevel === 0) return false

      // 获取店铺ID
      const shopId = RestaurantInfoManager.instance().shopId
      if (!shopId) return false

      // 获取会员菜品
      const memberItems = await this.getMemberItems(shopId, userMenu)

      // 没有会员菜品，不需要创建分类
      if (memberItems.length === 0) return false

      // 处理会员菜品并添加到菜单
      const processedMemberItems = this.processAndPrepareItems(memberItems)

      // 创建会员菜品分类并添加到菜单最前面
      orderMenu.categories.unshift(
        this.createMemberCategory(processedMemberItems)
      )
      // 标记为Circle Eats用户
      orderMenu.isCircleEatsUser = true
      return true
    } catch (error) {
      console.error('创建会员菜品分类失败', error)
      return false
    }
  }

  /**
   * 获取会员菜品列表
   * @param shopId 店铺ID
   * @param userMenu 用户菜单
   * @returns 会员菜品列表
   */
  private async getMemberItems(
    shopId: string,
    userMenu: UserMenu
  ): Promise<Item[]> {
    // 检查缓存中是否已有数据且未过期
    const cachedData = this._memberItemDataMap.get(shopId)
    const now = Date.now()

    if (
      cachedData &&
      cachedData.isMemberItemsLoaded &&
      cachedData.memberItems.length > 0 &&
      cachedData.lastUpdated &&
      now - cachedData.lastUpdated < this._config.cacheExpirationMs!
    ) {
      return cachedData.memberItems
    }

    // 筛选会员菜品
    const memberItems = userMenu.items.filter((item) =>
      item.categories.some((cat) => {
        const categoryId = Number(cat.categoryId)
        return (
          !isNaN(categoryId) && categoryId > 0 && categoryId === this._userLevel
        )
      })
    )

    // 更新缓存
    const threshold = await this.getMemberItemThreshold(shopId, this._userLevel)
    this._memberItemDataMap.set(shopId, {
      userLevel: this._userLevel,
      threshold,
      memberItems,
      isMemberItemsLoaded: true,
      lastUpdated: now
    })

    return memberItems
  }

  /**
   * 处理并准备会员菜品条目
   * @param memberItems 会员菜品列表
   * @returns 处理后的会员菜品列表
   */
  private processAndPrepareItems(memberItems: Item[]): Item[] {
    return memberItems.map((item) => {
      return {
        ...item,
        price: 0, // 设置价格为0
        itemAdditionalInformation: {
          ...item.itemAdditionalInformation,
          discountPrice: -1, // 重置折扣价为-1(表示无折扣)
          isMemberItem: true, // 标记为会员菜品
          memberItemLevel:
            item.categories.find((cat) => {
              const catId = Number(cat.categoryId)
              return !isNaN(catId) && catId > 0
            })?.categoryId || '1'
        }
      }
    })
  }

  /**
   * 创建会员菜品分类
   * @param items 会员菜品列表
   * @returns 会员菜品分类
   */
  private createMemberCategory(items: Item[]): OrderMenuCategory {
    return {
      items,
      // categoryId: CategoryType.MemberItem, // 因为现在只显示一个分类所以默认使用会员分类id
      categoryId: this._userLevel.toString(),
      name: MEMBER_ITEM_CATEGORY_NAME,
      translations: {}
    }
  }

  /**
   * 移除购物车中的会员菜品并提示用户
   * @param items 订单商品列表
   * @param removeCallback 移除商品的回调函数
   * @param t 国际化翻译函数
   */
  public removeMemberItemsFromCart(
    items: OrderedItemType[],
    removeCallback: (item: OrderedItemType) => void,
    t: (key: string) => string
  ): void {
    const memberItem = items.find((item) =>
      this.isMemberCategoryId(item.categoryId)
    )
    if (memberItem) {
      removeCallback(memberItem)
      this.setMemberItemInCart(false)
      message.info(t(MemberItemLocaleKeys.MEMBER_ITEM_REMOVED))
    }
  }

  /**
   * 检查会员菜品消费门槛并弹窗提示
   * @param options 参数对象
   * @returns 处理结果的Promise
   */
  public async checkAndHandleMemberItems(
    items: OrderedItemType[],
    removeCallback: (item: OrderedItemType) => void,
    continueCallback: () => void,
    t: (key: string, values?: any[]) => string,
    shopId?: string
  ): Promise<void> {
    try {
      // 确保初始化完成
      await this.ensureInitialized()

      // 查找购物车中的会员菜品
      const memberItem = items.find((item) =>
        this.isMemberCategoryId(item.categoryId)
      )
      if (!memberItem) {
        continueCallback()
        return
      }

      // 检查用户是否登录及是否可以查看会员菜品
      if (!this.canViewMemberItems()) {
        this.showLoginRequiredModal(
          memberItem,
          removeCallback,
          continueCallback,
          t
        )
        return
      }

      // 检查消费门槛
      const { passed, threshold } = await this.checkMemberItemThreshold(
        items,
        shopId
      )

      // 不满足门槛，提示并询问用户
      if (!passed) {
        this.showThresholdNotMetModal(
          memberItem,
          threshold,
          removeCallback,
          continueCallback,
          t
        )
        return
      }

      // 通过所有检查，继续结算
      continueCallback()
    } catch (error) {
      console.error('检查会员菜品消费门槛失败', error)
      // 出错时仍然允许继续流程
      continueCallback()
    }
  }

  /**
   * 显示登录提示弹窗
   */
  private showLoginRequiredModal(
    memberItem: OrderedItemType,
    removeCallback: (item: OrderedItemType) => void,
    continueCallback: () => void,
    t: (key: string, values?: any[]) => string
  ): void {
    Modal.confirm({
      title: t(MemberItemLocaleKeys.TIP),
      content: t(MemberItemLocaleKeys.PLEASE_LOGIN_TO_USE_MEMBER_ITEM),
      onOk: () => {
        // 移除会员菜品并继续结算
        removeCallback(memberItem)
        this.setMemberItemInCart(false)
        continueCallback()
      }
    })
  }

  /**
   * 显示门槛未满足提示弹窗
   */
  private showThresholdNotMetModal(
    memberItem: OrderedItemType,
    threshold: number,
    removeCallback: (item: OrderedItemType) => void,
    continueCallback: () => void,
    t: (key: string, values?: any[]) => string
  ): void {
    Modal.confirm({
      title: t(MemberItemLocaleKeys.TIP),
      content: t(MemberItemLocaleKeys.MEMBER_ITEM_THRESHOLD_MESSAGE, [
        threshold / 100
      ]),
      okText: t(MemberItemLocaleKeys.REMOVE_AND_CONTINUE),
      cancelText: t(MemberItemLocaleKeys.ADD_MORE),
      onOk: () => {
        // 用户确认，移除会员菜品并继续结算
        removeCallback(memberItem)
        this.setMemberItemInCart(false)
        message.info(t(MemberItemLocaleKeys.MEMBER_ITEM_REMOVED))
        setTimeout(() => {
          continueCallback()
        }, 500)
      }
    })
  }

  /**
   * 处理会员菜品的categoryId问题, 将本地设置的categoryId重置为真实的originalCategoryId
   * @param guestPayment 订单支付信息
   */
  public handleMemberItemCategoryId(items: OrderedItemType[]): void {
    if (!items?.length) return

    items.forEach((item) => {
      if (
        item.originalCategoryId &&
        this.isMemberCategoryId(item.originalCategoryId)
      ) {
        item.categoryId = item.originalCategoryId
        delete item.originalCategoryId
      }
    })
  }

  /**
   * 获取会员菜品提示信息
   * @param t 国际化翻译函数
   * @returns 提示信息
   */
  public getMessageTip(t: (key: string) => string): string {
    if (this._memberItemState.usedToday) {
      return t(MemberItemLocaleKeys.MEMBER_ITEM_USED_TODAY)
    }

    if (this._memberItemState.inUnpaidOrder) {
      return t(MemberItemLocaleKeys.CANNOT_ADD_MORE_MEMBER_ITEM)
    }

    // 默认提示
    return t(MemberItemLocaleKeys.ONLY_ONE_FOOD_ITEM)
  }

  /**
   * 清除缓存数据
   */
  public clearCache(): void {
    this._memberItemDataMap.clear()
    this._memberItemState = {
      inCart: false,
      inUnpaidOrder: false,
      countUnpaidOrder: 0,
      usedToday: false
    }
    this._userLevel = 0
    this._initialized = false
  }

  /**
   * 获取缓存状态
   * 用于调试和监控
   * @returns 缓存状态
   */
  public getCacheStats(): {
    shopCount: number
    itemsCount: number
    newestCacheTime: number | null
    oldestCacheTime: number | null
  } {
    let totalItems = 0
    let newestTime: number | null = null
    let oldestTime: number | null = null

    this._memberItemDataMap.forEach((data) => {
      totalItems += data.memberItems.length

      if (data.lastUpdated) {
        if (newestTime === null || data.lastUpdated > newestTime) {
          newestTime = data.lastUpdated
        }

        if (oldestTime === null || data.lastUpdated < oldestTime) {
          oldestTime = data.lastUpdated
        }
      }
    })

    return {
      shopCount: this._memberItemDataMap.size,
      itemsCount: totalItems,
      newestCacheTime: newestTime,
      oldestCacheTime: oldestTime
    }
  }

  /**
   * 添加事件监听器
   * @param eventType 事件类型
   * @param callback 回调函数
   */
  public addEventListener(
    eventType: MemberItemEventType,
    callback: MemberItemEventCallback
  ): void {
    if (!this._eventListeners.has(eventType)) {
      this._eventListeners.set(eventType, new Set())
    }

    this._eventListeners.get(eventType)!.add(callback)
  }

  /**
   * 移除事件监听器
   * @param eventType 事件类型
   * @param callback 回调函数
   */
  public removeEventListener(
    eventType: MemberItemEventType,
    callback: MemberItemEventCallback
  ): void {
    if (this._eventListeners.has(eventType)) {
      this._eventListeners.get(eventType)!.delete(callback)
    }
  }

  /**
   * 触发事件
   * @param eventType 事件类型
   * @param data 事件数据
   */
  private emitEvent(eventType: MemberItemEventType, data: any): void {
    if (this._eventListeners.has(eventType)) {
      this._eventListeners.get(eventType)!.forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error(`事件处理错误 (${eventType})`, error)
        }
      })
    }
  }
}
